import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Modal, Form, message, Popconfirm, Select, Tag, Checkbox, Upload, Drawer, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, FileTextOutlined, UploadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { judgeSetApi, businessScenarioApi, useCaseApi } from '../../services/api';
import { formatDateTime } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const { Search, TextArea } = Input;
const { Option } = Select;

function JudgeSetList() {
  const navigate = useNavigate();
  const { currentScenario, hasManagePermission } = useWorkspace();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState({
    setName: '',
    setCategory: undefined,
    businessScenarioId: undefined,
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  // 业务场景列表
  const [businessScenarios, setBusinessScenarios] = useState([]);

  // 评测集明细弹窗相关状态
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [currentJudgeSet, setCurrentJudgeSet] = useState(null);
  const [detailDataSource, setDetailDataSource] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPagination, setDetailPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [detailSearchParams, setDetailSearchParams] = useState({
    caseName: '',
    tags: '',
  });
  const [selectedDetailRowKeys, setSelectedDetailRowKeys] = useState([]);

  // 添加用例弹窗相关状态
  const [addCaseModalVisible, setAddCaseModalVisible] = useState(false);
  const [addCaseDataSource, setAddCaseDataSource] = useState([]);
  const [addCaseLoading, setAddCaseLoading] = useState(false);
  const [addCasePagination, setAddCasePagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [addCaseSearchParams, setAddCaseSearchParams] = useState({
    caseName: '',
    attachmentType: undefined,
    tags: '',
  });
  const [selectedAddCaseRowKeys, setSelectedAddCaseRowKeys] = useState([]);
  const [selectAllInProgress, setSelectAllInProgress] = useState(false);
  const [isSelectAllMode, setIsSelectAllMode] = useState(false);

  // 编辑用例弹窗相关状态
  const [editCaseModalVisible, setEditCaseModalVisible] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [editCaseForm] = Form.useForm();
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [fileTypes, setFileTypes] = useState([]); // 文件类型列表
  const [addCaseFileTypeOptions, setAddCaseFileTypeOptions] = useState([]); // 添加用例弹窗的文件类型选项
  const [addCaseFileTypeLoading, setAddCaseFileTypeLoading] = useState(false);

  // 来源选项
  const sources = ['线上', '手工'];

  // 如果没有选择业务场景，跳转到业务场景列表
  useEffect(() => {
    if (!currentScenario) {
      message.warning('请先选择业务场景');
      navigate('/business-scenario');
    }
  }, [currentScenario, navigate]);

  // 评测集分类
  const setCategories = ['基线评测集', '评估验收评测集', '线上周期采样评测集'];



  useEffect(() => {
    fetchBusinessScenarios();
    fetchData();
  }, [pagination.current, pagination.pageSize, searchParams]);

  const fetchData = async () => {
    if (!currentScenario) return;

    setLoading(true);
    try {
      const params = {
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
        setName: searchParams.setName || undefined,
        setCategory: searchParams.setCategory || undefined,
        businessScenarioId: currentScenario.id, // 使用当前业务场景的ID
      };
      
      const response = await judgeSetApi.getList(params);
      
      setDataSource(response.list || []);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0,
      }));
    } catch (error) {
      console.error('获取评测集列表失败:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchBusinessScenarios = async () => {
    try {
      const response = await businessScenarioApi.getList({ pageSize: 1000 });
      setBusinessScenarios(response.list || []);
    } catch (error) {
      console.error('获取业务场景列表失败:', error);
      setBusinessScenarios([]);
    }
  };

  const handleSearch = (value) => {
    setSearchParams(prev => ({ ...prev, setName: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleCategoryFilter = (setCategory) => {
    setSearchParams(prev => ({ ...prev, setCategory }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleAdd = () => {
    setEditingRecord(null);
    setModalVisible(true);
    form.resetFields();
    // 如果有当前业务场景，自动设置
    if (currentScenario) {
      form.setFieldsValue({
        businessScenarioId: currentScenario.id,
      });
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setModalVisible(true);
    form.setFieldsValue({
      setName: record.setName,
      setCategory: record.setCategory,
      businessScenarioId: record.businessScenarioId,
      description: record.description,
    });
  };

  const handleDelete = async (id) => {
    try {
      await judgeSetApi.delete(id);
      message.success('删除成功');
      fetchData();
    } catch (error) {
      console.error('删除评测集失败:', error);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingRecord) {
        await judgeSetApi.update(editingRecord.id, values);
        message.success('修改成功');
      } else {
        await judgeSetApi.create(values);
        message.success('新增成功');
      }
      
      setModalVisible(false);
      fetchData();
    } catch (error) {
      console.error('保存评测集失败:', error);
    }
  };

  const handleTableChange = (paginationConfig) => {
    setPagination(paginationConfig);
  };

  // 打开评测集明细弹窗
  const handleManage = (record) => {
    setCurrentJudgeSet(record);
    setDetailModalVisible(true);
    setDetailSearchParams({ caseName: '', tags: '' });
    setDetailPagination({ current: 1, pageSize: 10, total: 0 });
    setSelectedDetailRowKeys([]);
  };

  // 获取评测集明细列表
  const fetchDetailData = async () => {
    if (!currentJudgeSet) return;

    setDetailLoading(true);
    try {
      const params = {
        pageNum: detailPagination.current,
        pageSize: detailPagination.pageSize,
        caseName: detailSearchParams.caseName || undefined,
        tags: detailSearchParams.tags || undefined,
      };

      const response = await useCaseApi.getByJudgeSet(currentJudgeSet.id, params);

      setDetailDataSource(response.list || []);
      setDetailPagination(prev => ({
        ...prev,
        total: response.total || 0,
      }));
    } catch (error) {
      console.error('获取评测集明细失败:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  // 监听评测集明细弹窗打开和搜索参数变化
  useEffect(() => {
    if (detailModalVisible && currentJudgeSet) {
      fetchDetailData();
    }
  }, [detailModalVisible, currentJudgeSet, detailPagination.current, detailPagination.pageSize, detailSearchParams]);

  // 评测集明细表格筛选
  const handleDetailSearch = (field, value) => {
    setDetailSearchParams(prev => ({ ...prev, [field]: value }));
    setDetailPagination(prev => ({ ...prev, current: 1 }));
  };

  // 评测集明细表格分页变化
  const handleDetailTableChange = (paginationConfig) => {
    setDetailPagination(paginationConfig);
  };

  // 打开添加用例弹窗
  const handleAddCase = () => {
    setAddCaseModalVisible(true);
    setAddCaseSearchParams({ caseName: '', attachmentType: undefined, tags: '' });
    setAddCasePagination({ current: 1, pageSize: 10, total: 0 });
    setSelectedAddCaseRowKeys([]);
    // 获取文件类型选项
    fetchAddCaseFileTypes();
  };

  // 获取添加用例弹窗的文件类型选项
  const fetchAddCaseFileTypes = async () => {
    if (!currentScenario?.id) return;
    
    setAddCaseFileTypeLoading(true);
    try {
      const response = await useCaseApi.getFileTypesByScenario(currentScenario.id);
      setAddCaseFileTypeOptions(response || []);
    } catch (error) {
      console.error('获取文件类型列表失败:', error);
      setAddCaseFileTypeOptions([]);
    } finally {
      setAddCaseFileTypeLoading(false);
    }
  };

  // 获取可添加的用例列表
  const fetchAddCaseData = async () => {
    if (!currentScenario) return;

    setAddCaseLoading(true);
    try {
      const params = {
        pageNum: addCasePagination.current,
        pageSize: addCasePagination.pageSize,
        scenarioId: currentScenario.id,
        caseName: addCaseSearchParams.caseName || undefined,
        attachmentType: addCaseSearchParams.attachmentType || undefined,
        tags: addCaseSearchParams.tags || undefined,
      };

      const response = await useCaseApi.getList(params);

      setAddCaseDataSource(response.list || []);
      setAddCasePagination(prev => ({
        ...prev,
        total: response.total || 0,
      }));
    } catch (error) {
      console.error('获取用例列表失败:', error);
    } finally {
      setAddCaseLoading(false);
    }
  };

  // 监听添加用例弹窗打开和搜索参数变化
  useEffect(() => {
    if (addCaseModalVisible) {
      fetchAddCaseData();
    }
  }, [addCaseModalVisible, addCasePagination.current, addCasePagination.pageSize, addCaseSearchParams]);

  // 添加用例表格分页变化
  const handleAddCaseTableChange = (paginationConfig) => {
    // 切换页码时，保留已选择的用例ID
    setAddCasePagination(paginationConfig);
  };

  // 处理表格行选择变化（使用 preserveSelectedRowKeys 实现跨页选择）
  const handleAddCaseRowSelectionChange = (selectedKeys) => {
    setSelectedAddCaseRowKeys(selectedKeys);
  };

  // 确认添加用例
  const handleAddCaseConfirm = async () => {
    // 如果是全选模式，调用后端接口按业务场景ID批量绑定
    if (isSelectAllMode) {
      setSelectAllInProgress(true);
      try {
        // 异步调用，立即返回taskId
        const response = await useCaseApi.batchBindJudgeSetsByScenario({
          setIds: [currentJudgeSet.id],
          scenarioId: currentScenario.id,
        });
        
        // 提示用户任务已提交
        message.success({
          content: '✅ 批量添加任务已提交，正在后台处理，请稍后刷新查看结果',
          duration: 5
        });
        
        setAddCaseModalVisible(false);
        setIsSelectAllMode(false);
        setSelectedAddCaseRowKeys([]);
        
        // 3秒后自动刷新评测集明细列表
        setTimeout(() => {
          fetchDetailData();
        }, 3000);
        
      } catch (error) {
        console.error('添加所有用例失败:', error);
        message.error('添加所有用例失败');
      } finally {
        setSelectAllInProgress(false);
      }
      return;
    }

    // 普通模式：检查是否有选中的用例
    if (selectedAddCaseRowKeys.length === 0) {
      message.warning('请至少选择一个用例');
      return;
    }

    try {
      await useCaseApi.batchBindJudgeSets({
        setIds: [currentJudgeSet.id],
        caseIds: selectedAddCaseRowKeys,
      });
      message.success('添加用例成功');
      setAddCaseModalVisible(false);
      setSelectedAddCaseRowKeys([]);
      fetchDetailData(); // 刷新评测集明细列表
    } catch (error) {
      console.error('添加用例失败:', error);
      message.error('添加用例失败');
    }
  };

  // 批量解绑用例
  const handleBatchUnbind = async () => {
    if (selectedDetailRowKeys.length === 0) {
      message.warning('请至少选择一个用例');
      return;
    }

    Modal.confirm({
      title: '确认解绑',
      content: `确定要解绑选中的 ${selectedDetailRowKeys.length} 个用例吗？`,
      onOk: async () => {
        try {
          // 使用数组方式传参
          await useCaseApi.unbindJudgeSet({
            setIds: [currentJudgeSet.id],
            caseIds: selectedDetailRowKeys,
          });
          message.success('解绑成功');
          setSelectedDetailRowKeys([]);
          fetchDetailData();
        } catch (error) {
          console.error('解绑用例失败:', error);
        }
      },
    });
  };

  // 删除单个用例
  const handleDeleteCase = async (caseId) => {
    try {
      // 使用数组方式传参
      await useCaseApi.unbindJudgeSet({
        setIds: [currentJudgeSet.id],
        caseIds: [caseId],
      });
      message.success('删除成功');
      fetchDetailData();
    } catch (error) {
      console.error('删除用例失败:', error);
    }
  };

  // 打开编辑用例弹窗
  const handleEditCase = async (record) => {
    setEditingCase(record);
    setEditCaseModalVisible(true);

    // 设置表单初始值
    editCaseForm.setFieldsValue({
      caseName: record.caseName,
      scenarioId: record.scenarioId,
      documentId: record.documentId,
      source: record.source,
      tags: record.tags,
      hasUseModel: record.hasUseModel !== undefined ? record.hasUseModel : 'n',
      businessExtInfo: record.businessExtInfo ? (typeof record.businessExtInfo === 'object' ? JSON.stringify(record.businessExtInfo, null, 2) : record.businessExtInfo) : '',
      expectedAnswer: record.expectedAnswer ? (typeof record.expectedAnswer === 'object' ? JSON.stringify(record.expectedAnswer, null, 2) : record.expectedAnswer) : '',
      attachmentId: record.attachmentId,
    });

    // 处理附件信息 - 获取完整的附件详情以保证提交参数一致
    if (record.id) {
      try {
        // 并行获取附件信息和文件类型列表
        const [attachmentInfo, fileTypesResponse] = await Promise.all([
          useCaseApi.getAttachment(record.id),
          useCaseApi.getFileTypes([record.id])
        ]);

        if (fileTypesResponse) {
          setFileTypes(fileTypesResponse || []);
        }

        if (attachmentInfo) {
          // 1. 更新表单值，使 UI 显示文件名和文件类型
          editCaseForm.setFieldsValue({
            attachmentType: attachmentInfo.attachmentType || '',
            attachment: [{
              uid: String(attachmentInfo.id),
              name: attachmentInfo.fileName || attachmentInfo.name,
              status: 'done',
              url: attachmentInfo.downloadUrl || attachmentInfo.url,
            }]
          });

          // 2. 更新 editingCase 状态存储详情，用于提交
          setEditingCase(prev => ({
            ...prev,
            attachmentName: attachmentInfo.fileName || attachmentInfo.name,
            attachmentUrl: attachmentInfo.downloadUrl || attachmentInfo.url,
            attachmentType: attachmentInfo.attachmentType
          }));
        }
      } catch (error) {
        console.error('获取用例附件或文件类型信息失败:', error);
      }
    }
  };

  // 确认编辑用例
  const handleEditCaseConfirm = async () => {
    try {
      const values = await editCaseForm.validateFields();

      // 构建用例数据 (useCase 内部字段全部对齐示例)
      const useCaseData = {
        caseName: values.caseName,
        scenarioId: editingCase?.scenarioId,
        documentId: values.documentId || '',
        source: values.source,
        tags: values.tags || '',
        hasUseModel: values.hasUseModel !== undefined ? values.hasUseModel : 'n',
        businessExtInfo: values.businessExtInfo || '',
        expectedAnswer: values.expectedAnswer || ''
      };

      // 构建附件信息 (从存储的 editingCase 中获取补全的附件详情)
      const attachmentInfo = {
        attachmentId: values.attachmentId || editingCase?.attachmentId,
        attachmentName: editingCase?.attachmentName || '',
        attachmentUrl: editingCase?.attachmentUrl || '',
        attachmentType: values.attachmentType || editingCase?.attachmentType || ''
      };

      // 构建最终提交数据（包裹结构）
      const submitData = {
        useCase: useCaseData,
        attachment: attachmentInfo
      };

      await useCaseApi.update(editingCase.id, submitData);
      message.success('修改成功');
      setEditCaseModalVisible(false);
      fetchDetailData();
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      console.error('修改用例失败:', error);
    }
  };

  // 附件上传配置
  const attachmentUploadProps = {
    name: 'file',
    maxCount: 1,
    beforeUpload: (file) => {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('文件大小不能超过10MB');
        return Upload.LIST_IGNORE;
      }
      return false;
    },
    onChange: async (info) => {
      if (info.fileList.length > 0) {
        const file = info.fileList[0].originFileObj;
        if (file) {
          setAttachmentUploading(true);
          try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await useCaseApi.uploadFile(formData, currentScenario?.id);
            editCaseForm.setFieldsValue({ attachmentId: response.id });
            message.success('附件上传成功');
          } catch (error) {
            console.error('附件上传失败:', error);
          } finally {
            setAttachmentUploading(false);
          }
        }
      }
    },
  };

  // 判断是否可以编辑用例
  const canEditCase = (record) => {
    if (!currentScenario || !record) return false;

    // 条件1：用例归属的业务场景就是当前进入的业务场景
    if (record.scenarioId === currentScenario.id) {
      return true;
    }

    // 条件2：当前进入的业务场景在可修改业务场景列表中
    // actionableBusinessId 格式如 ",4," 或 ",4,5,6,"
    const actionableBusinessId = record.actionableBusinessId || '';
    if (actionableBusinessId) {
      // 解析可修改业务场景ID列表
      const editableScenarioIds = actionableBusinessId
        .split(',')
        .filter(id => id.trim() !== '')
        .map(id => parseInt(id.trim(), 10))
        .filter(id => !isNaN(id));

      return editableScenarioIds.includes(currentScenario.id);
    }

    return false;
  };

  const getCategoryColor = (category) => {
    const colorMap = {
      '基线评测集': 'blue',
      '评估验收评测集': 'green',
      '线上周期采样评测集': 'orange'
    };
    return colorMap[category] || 'default';
  };

  // 根据业务场景分类获取背景色
  const getScenarioCategoryColor = (scenarioCategory) => {
    const colorMap = {
      'FEATURE_EXTRACTION': 'blue',      // 要素抽取 → 蓝色
      'AUTO_REVIEW': 'green',          // 机审 → 绿色
      'ROBOT_QA': 'orange',              // 机器人问答 → 橙色
    };
    return colorMap[scenarioCategory] || 'default';
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '评测集名称',
      dataIndex: 'setName',
      key: 'setName',
      width: 250,
    },
    {
      title: '评测集分类',
      dataIndex: 'setCategory',
      key: 'setCategory',
      width: 150,
      render: (category) => (
        <Tag color={getCategoryColor(category)}>{category}</Tag>
      ),
    },

    {
      title: '说明',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      render: (time) => formatDateTime(time),
    },
    {
      title: '创建人',
      dataIndex: 'creatorInfo',
      key: 'creator',
      width: 150,
      render: (creatorInfo) => {
        if (!creatorInfo) return '-';

        const { name, workNo, nickName } = creatorInfo;
        const displayName = name || nickName || '';
        const link = workNo ? `https://work.alibaba-inc.com/nwpipe/u/${workNo}` : '';

        if (link && displayName) {
          return (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700"
            >
              {displayName}{workNo ? `(${workNo})` : ''}
            </a>
          );
        }

        return displayName || '-';
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      fixed: 'right',
      render: (_, record) => {
        // 判断当前业务场景是否在 actionableBusinessId 中
        const actionableBusinessId = record.actionableBusinessId || '';
        const hasEditPermission = actionableBusinessId && currentScenario
          ? actionableBusinessId.includes(`,${currentScenario.id},`)
          : true; // 如果没有 actionableBusinessId 字段，默认有权限

        return (
          <Space size="small">
            {(currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' || currentScenario?.scenarioCategory === 'AUTO_REVIEW') && hasManagePermission() && (
              <Button
                size="small"
                icon={<FileTextOutlined />}
                onClick={() => handleManage(record)}
                style={{
                  background: '#E5F5FF',
                  border: '1px solid #91D5FF',
                  color: '#1890FF'
                }}
              >
                管理
              </Button>
            )}
            {hasEditPermission && hasManagePermission() && (
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                style={{
                  background: '#E5F5FF',
                  border: '1px solid #91D5FF',
                  color: '#1890FF'
                }}
              >
                编辑
              </Button>
            )}
            {hasManagePermission() && (
              <Popconfirm
                title="确定要删除这个评测集吗？"
                description="删除后将无法恢复，请谨慎操作"
                onConfirm={() => handleDelete(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button
                  size="small"
                  icon={<DeleteOutlined />}
                  className="table-action-btn-delete"
                >
                  删除
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">评测集管理</h1>
        <p className="page-description">管理评测用例集合，包括基线评测集、验收评测集和线上采样评测集</p>
      </div>

      <div className="mb-4 flex justify-between items-center">
        <Space size="middle">
          <Search
            placeholder="请输入评测集名称"
            allowClear
            suffix={<SearchOutlined />}
            size="large"
            className="w-80"
            onSearch={handleSearch}
          />
          <Select
            placeholder="选择评测集分类"
            allowClear
            size="large"
            className="w-48"
            onChange={handleCategoryFilter}
          >
            {setCategories.map(category => (
              <Option key={category} value={category}>
                {category}
              </Option>
            ))}
          </Select>
        </Space>
        {hasManagePermission() && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={handleAdd}
            style={{
              background: '#1890FF',
              borderColor: '#1890FF',
              boxShadow: 'none'
            }}
          >
            新增评测集
          </Button>
        )}
      </div>

      <div>
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条/总共 ${total} 条`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1400 }}
        />
      </div>

      <Modal
        title={editingRecord ? '编辑评测集' : '新增评测集'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          className="mt-4"
        >
          <Form.Item
            name="setName"
            label="评测集名称"
            rules={[
              { required: true, message: '请输入评测集名称' },
              { max: 100, message: '评测集名称不能超过100个字符' },
            ]}
          >
            <Input placeholder="请输入评测集名称" />
          </Form.Item>
          
          <Form.Item
            name="setCategory"
            label="评测集分类"
            rules={[
              { required: true, message: '请选择评测集分类' },
            ]}
          >
            <Select placeholder="请选择评测集分类" disabled={!!editingRecord}>
              {setCategories.map(category => (
                <Option key={category} value={category}>
                  {category}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="businessScenarioId"
            label="业务场景"
            rules={[
              { required: true, message: '请选择业务场景' },
            ]}
          >
            <Select placeholder="请选择业务场景" disabled={true}>
              {businessScenarios.map(scenario => (
                <Option key={scenario.id} value={scenario.id}>
                  {scenario.scenarioName}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="description"
            label="评测集说明"
          >
            <TextArea 
              rows={4} 
              placeholder="请输入评测集说明" 
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 评测集明细抽屉 */}
      <Drawer
        title={null}
        placement="right"
        open={detailModalVisible}
        onClose={() => setDetailModalVisible(false)}
        width="100%"
        destroyOnClose
        styles={{
          header: { display: 'none' },
          body: { padding: 0 }
        }}
      >
        <div style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #FFE5E5 0%, #FFF0E5 14.28%, #FFFBE5 28.56%, #F0FFE5 42.84%, #E5F5FF 57.12%, #EBE5FF 71.4%, #F5E5FF 85.68%, #FFE5F5 100%)'
        }}>
          {/* 顶部导航栏 */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            padding: '16px 32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => setDetailModalVisible(false)}
                style={{ fontSize: '16px' }}
              >
                返回
              </Button>
              <div style={{
                height: '24px',
                width: '1px',
                background: '#e8e8e8'
              }} />
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: 600,
                color: '#000000'
              }}>
                评测集明细 - {currentJudgeSet?.setName || ''}
              </h2>
            </div>
          </div>

          {/* 主内容区域 */}
          <div style={{ padding: '24px 32px' }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.98)',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
              minHeight: 'calc(100vh - 140px)'
            }}>
              {/* 搜索和操作栏 */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
                padding: '16px 20px',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)',
                borderRadius: '12px'
              }}>
                <Space size="middle">
                  <Input
                    placeholder="请输入用例名称"
                    allowClear
                    value={detailSearchParams.caseName}
                    onChange={(e) => handleDetailSearch('caseName', e.target.value)}
                    onPressEnter={() => handleDetailSearch('caseName', detailSearchParams.caseName)}
                    style={{ width: 250, borderRadius: '8px' }}
                  />
                  <Input
                    placeholder="请输入标签"
                    allowClear
                    value={detailSearchParams.tags}
                    onChange={(e) => handleDetailSearch('tags', e.target.value)}
                    onPressEnter={() => handleDetailSearch('tags', detailSearchParams.tags)}
                    style={{ width: 200, borderRadius: '8px' }}
                  />
                </Space>
                <Space>
                  {hasManagePermission() && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleAddCase}
                      style={{
                        borderRadius: '8px',
                        background: '#1890FF',
                        borderColor: '#1890FF',
                        boxShadow: 'none'
                      }}
                    >
                      添加用例
                    </Button>
                  )}
                  {hasManagePermission() && (
                    <Button
                      danger
                      disabled={selectedDetailRowKeys.length === 0}
                      onClick={handleBatchUnbind}
                      style={{ borderRadius: '8px' }}
                    >
                      解绑用例 ({selectedDetailRowKeys.length})
                    </Button>
                  )}
                </Space>
              </div>

              {/* 表格 */}
              <Table
                columns={[
                  {
                    title: '用例ID',
                    dataIndex: 'id',
                    key: 'id',
                    width: 80,
                  },
                  {
                    title: '用例名称',
                    dataIndex: 'caseName',
                    key: 'caseName',
                    width: 250,
                    ellipsis: true,
                  },
                  {
                    title: '业务场景',
                    dataIndex: 'scenarioName',
                    key: 'scenarioName',
                    width: 150,
                    render: (text, record) => {
                      if (!text) return '-';
                      const color = getScenarioCategoryColor(record.scenarioCategory);
                      return <Tag color={color} style={{ borderRadius: '4px' }}>{text}</Tag>;
                    },
                  },
                  {
                    title: '单据标识',
                    dataIndex: 'documentId',
                    key: 'documentId',
                    width: 150,
                  },
                  {
                    title: '来源',
                    dataIndex: 'source',
                    key: 'source',
                    width: 100,
                  },
                  {
                    title: '标签',
                    dataIndex: 'tags',
                    key: 'tags',
                    width: 200,
                    render: (tags) => {
                      if (!tags) return '-';
                      const tagList = tags.split(',').filter(t => t.trim());
                      return tagList.map((tag, index) => (
                        <Tag key={index} color="blue" style={{ borderRadius: '4px' }}>{tag.trim()}</Tag>
                      ));
                    },
                  },
                  {
                    title: '操作',
                    key: 'action',
                    width: 180,
                    fixed: 'right',
                    render: (_, record) => (
                      <Space size="small">
                        {canEditCase(record) && hasManagePermission() && (
                          <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEditCase(record)}
                            style={{
                              background: '#E5F5FF',
                              border: '1px solid #91D5FF',
                              color: '#1890FF'
                            }}
                          >
                            编辑
                          </Button>
                        )}
                        {hasManagePermission() && (
                          <Popconfirm
                            title="确定要删除这个用例吗？"
                            onConfirm={() => handleDeleteCase(record.id)}
                            okText="确定"
                            cancelText="取消"
                          >
                            <Button
                              size="small"
                              icon={<DeleteOutlined />}
                              className="table-action-btn-delete"
                            >
                              删除
                            </Button>
                          </Popconfirm>
                        )}
                      </Space>
                    ),
                  },
                ]}
                dataSource={detailDataSource}
                rowKey="id"
                loading={detailLoading}
                rowSelection={{
                  selectedRowKeys: selectedDetailRowKeys,
                  preserveSelectedRowKeys: true,
                  onChange: (selectedKeys) => setSelectedDetailRowKeys(selectedKeys),
                }}
                pagination={{
                  ...detailPagination,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `第 ${range[0]}-${range[1]} 条/总共 ${total} 条`,
                }}
                onChange={handleDetailTableChange}
                scroll={{ x: 1200, y: 'calc(100vh - 380px)' }}
                style={{ borderRadius: '8px' }}
              />
            </div>
          </div>
        </div>
      </Drawer>

      {/* 添加用例抽屉 */}
      <Drawer
        title={null}
        placement="right"
        open={addCaseModalVisible}
        onClose={() => {
          setAddCaseModalVisible(false);
          setSelectedAddCaseRowKeys([]);
          setIsSelectAllMode(false);
        }}
        width="100%"
        destroyOnClose
        styles={{
          header: { display: 'none' },
          body: { padding: 0 }
        }}
      >
        <div style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #FFE5E5 0%, #FFF0E5 14.28%, #FFFBE5 28.56%, #F0FFE5 42.84%, #E5F5FF 57.12%, #EBE5FF 71.4%, #F5E5FF 85.68%, #FFE5F5 100%)'
        }}>
          {/* 顶部导航栏 */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            padding: '16px 32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => setAddCaseModalVisible(false)}
                style={{ fontSize: '16px' }}
              >
                返回
              </Button>
              <div style={{
                height: '24px',
                width: '1px',
                background: '#e8e8e8'
              }} />
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: 600,
                color: '#000000'
              }}>
                添加用例
              </h2>
            </div>
            <Space>
              <Button onClick={() => setAddCaseModalVisible(false)} style={{ borderRadius: '8px' }}>
                取消
              </Button>
              <Button
                onClick={() => {
                  // 一键全选：设置全选模式，但不立即提交
                  setIsSelectAllMode(true);
                  // 清空手动选择，提示用户点击"确定添加"
                  setSelectedAddCaseRowKeys([]);
                  message.info('已选择一键全选模式，点击"确定添加"将添加该业务场景下所有用例');
                }}
                loading={selectAllInProgress}
                disabled={selectAllInProgress}
                style={{
                  borderRadius: '8px',
                  background: isSelectAllMode ? '#52c41a' : undefined,
                  borderColor: isSelectAllMode ? '#52c41a' : undefined,
                  color: isSelectAllMode ? 'white' : undefined,
                }}
              >
                {isSelectAllMode ? '✓ 已选全部' : '一键全选'}
              </Button>
              <Button
                type="primary"
                onClick={handleAddCaseConfirm}
                disabled={(selectedAddCaseRowKeys.length === 0 && !isSelectAllMode) || selectAllInProgress}
                style={{
                  borderRadius: '8px',
                  background: '#1890FF',
                  borderColor: '#1890FF',
                  boxShadow: 'none'
                }}
              >
                {isSelectAllMode ? '确定添加 (所有用例)' : `确定添加 (${selectedAddCaseRowKeys.length})`}
              </Button>
            </Space>
          </div>

          {/* 主内容区域 */}
          <div style={{ padding: '24px 32px' }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.98)',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
              minHeight: 'calc(100vh - 140px)'
            }}>
              {/* 搜索栏 */}
              <div style={{
                marginBottom: '24px',
                padding: '16px 20px',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)',
                borderRadius: '12px'
              }}>
                <Space size="middle">
                  <Input
                    placeholder="请输入用例名称搜索"
                    allowClear
                    prefix={<SearchOutlined style={{ color: '#999' }} />}
                    value={addCaseSearchParams.caseName}
                    onChange={(e) => setAddCaseSearchParams(prev => ({ ...prev, caseName: e.target.value }))}
                    onPressEnter={() => setAddCasePagination(prev => ({ ...prev, current: 1 }))}
                    style={{ width: 280, borderRadius: '8px' }}
                  />
                  <Select
                    placeholder="文件类型"
                    allowClear
                    value={addCaseSearchParams.attachmentType}
                    onChange={(value) => {
                      setAddCaseSearchParams(prev => ({ ...prev, attachmentType: value }));
                      setAddCasePagination(prev => ({ ...prev, current: 1 }));
                    }}
                    loading={addCaseFileTypeLoading}
                    style={{ width: 150, borderRadius: '8px' }}
                  >
                    {addCaseFileTypeOptions.map(opt => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.text}
                      </Option>
                    ))}
                  </Select>
                  <Input
                    placeholder="请输入标签"
                    allowClear
                    value={addCaseSearchParams.tags}
                    onChange={(e) => setAddCaseSearchParams(prev => ({ ...prev, tags: e.target.value }))}
                    onPressEnter={() => setAddCasePagination(prev => ({ ...prev, current: 1 }))}
                    style={{ width: 200, borderRadius: '8px' }}
                  />
                </Space>
              </div>

              {/* 已选择提示 */}
              {selectedAddCaseRowKeys.length > 0 && (
                <div style={{
                  marginBottom: '16px',
                  padding: '12px 16px',
                  background: 'linear-gradient(135deg, #ede7f6 0%, #e1bee7 100%)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ color: '#7b1fa2', fontWeight: 500 }}>
                    ✓ 已选择 {selectedAddCaseRowKeys.length} 个用例
                  </span>
                </div>
              )}

              {/* 表格 */}
              <Table
                columns={[
                  {
                    title: '用例ID',
                    dataIndex: 'id',
                    key: 'id',
                    width: 80,
                  },
                  {
                    title: '用例名称',
                    dataIndex: 'caseName',
                    key: 'caseName',
                    width: 250,
                    ellipsis: true,
                  },
                  {
                    title: '业务场景',
                    dataIndex: 'scenarioName',
                    key: 'scenarioName',
                    width: 150,
                    render: (text, record) => {
                      if (!text) return '-';
                      const color = getScenarioCategoryColor(record.scenarioCategory);
                      return <Tag color={color} style={{ borderRadius: '4px' }}>{text}</Tag>;
                    },
                  },
                  {
                    title: '评测集名称',
                    dataIndex: 'judgeSetName',
                    key: 'judgeSetName',
                    width: 180,
                    ellipsis: true,
                    render: (text) => {
                      if (!text) return '-';
                      return (
                        <Tooltip title={text}>
                          <Tag color="green" style={{ borderRadius: '4px', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</Tag>
                        </Tooltip>
                      );
                    },
                  },
                  {
                    title: '单据标识',
                    dataIndex: 'documentId',
                    key: 'documentId',
                    width: 150,
                  },
                  {
                    title: '来源',
                    dataIndex: 'source',
                    key: 'source',
                    width: 100,
                  },
                ]}
                dataSource={addCaseDataSource}
                rowKey="id"
                loading={addCaseLoading}
                rowSelection={{
                  selectedRowKeys: selectedAddCaseRowKeys,
                  preserveSelectedRowKeys: true,
                  onChange: handleAddCaseRowSelectionChange,
                  getCheckboxProps: (record) => ({
                    disabled: false,
                  }),
                }}
                pagination={{
                  ...addCasePagination,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `第 ${range[0]}-${range[1]} 条/总共 ${total} 条`,
                }}
                onChange={handleAddCaseTableChange}
                scroll={{ x: 1080, y: 'calc(100vh - 380px)' }}
                style={{ borderRadius: '8px' }}
              />
            </div>
          </div>
        </div>
      </Drawer>

      {/* 编辑用例弹窗 */}
      <Modal
        title="编辑用例"
        open={editCaseModalVisible}
        onCancel={() => setEditCaseModalVisible(false)}
        onOk={handleEditCaseConfirm}
        width={700}
        destroyOnClose
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={editCaseForm}
          layout="vertical"
          className="mt-4"
        >
          <Form.Item
            name="caseName"
            label="用例名称"
            rules={[
              { required: true, message: '请输入用例名称' },
              { max: 200, message: '用例名称不能超过200个字符' },
            ]}
          >
            <Input placeholder="请输入用例名称" />
          </Form.Item>

          <Form.Item
            name="scenarioId"
            label="业务场景"
            rules={[
              { required: true, message: '请选择业务场景' },
            ]}
          >
            <Select
              placeholder="请选择业务场景"
              disabled={true}
            >
              {businessScenarios.map(scenario => (
                <Option key={scenario.id} value={scenario.id}>
                  {scenario.scenarioName}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* 要素抽取和机审场景：显示文件类型字段 */}
          {(currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' || currentScenario?.scenarioCategory === 'AUTO_REVIEW') && (
            <Form.Item
              name="attachmentType"
              label="文件类型"
            >
              <Input placeholder="请输入文件类型，填写ALE中文件类型Code" />
            </Form.Item>
          )}

          <Form.Item
            name="documentId"
            label="单据唯一标识"
          >
            <Input placeholder="请输入单据唯一标识" />
          </Form.Item>

          <Form.Item
            name="source"
            label="来源"
            rules={[
              { required: true, message: '请选择来源' },
            ]}
          >
            <Select placeholder="请选择来源">
              {sources.map(source => (
                <Option key={source} value={source}>
                  {source}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="tags"
            label="标签"
            extra="多个标签用英文逗号分隔"
          >
            <Input placeholder="请输入标签，多个用逗号分隔" />
          </Form.Item>
          
          <Form.Item
            name="hasUseModel"
            label="是否使用模型评测"
            initialValue="n"
          >
            <Select placeholder="请选择是否使用模型评测">
              <Option value="n">否</Option>
              <Option value="y">是</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="businessExtInfo"
            label="业务扩展信息"
            extra="请输入有效的JSON格式数据"
          >
            <TextArea
              rows={4}
              placeholder='{"key1": "value1", "key2": "value2"}'
              style={{ fontFamily: 'Monaco, Consolas, monospace' }}
            />
          </Form.Item>

          <Form.Item
            name="expectedAnswer"
            label="期望答案"
            extra="请输入有效的JSON格式数据"
          >
            <TextArea
              rows={4}
              placeholder='{"result": "expected_result", "score": 0.95}'
              style={{ fontFamily: 'Monaco, Consolas, monospace' }}
            />
          </Form.Item>

          {/* 编辑时显示可见业务场景（只读），仅要素抽取场景显示（机审场景不显示） */}
          {editingCase && currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' && (
            <Form.Item
              label="可见业务场景"
            >
              <div style={{ padding: '4px 11px', background: '#f5f5f5', borderRadius: '6px', minHeight: '32px' }}>
                {(() => {
                  const visibleBusinessIdStr = editingCase.visibleBusinessId || '';
                  const visibleIds = visibleBusinessIdStr
                    .split(',')
                    .filter(id => id.trim() !== '')
                    .map(id => parseInt(id.trim(), 10))
                    .filter(id => !isNaN(id));

                  if (visibleIds.length === 0) {
                    return <span style={{ color: '#999' }}>暂无配置</span>;
                  }

                  const visibleNames = visibleIds
                    .map(id => {
                      const scenario = businessScenarios.find(s => s.id === id);
                      return scenario ? scenario.scenarioName : null;
                    })
                    .filter(name => name !== null);

                  return visibleNames.length > 0 ? visibleNames.join('、') : <span style={{ color: '#999' }}>暂无配置</span>;
                })()}
              </div>
            </Form.Item>
          )}

          {/* 编辑时显示可修改业务场景（只读），仅要素抽取场景显示（机审场景不显示） */}
          {editingCase && currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' && (
            <Form.Item
              label="可修改业务场景"
            >
              <div style={{ padding: '4px 11px', background: '#f5f5f5', borderRadius: '6px', minHeight: '32px' }}>
                {(() => {
                  const actionableBusinessIdStr = editingCase.actionableBusinessId || '';
                  const actionableIds = actionableBusinessIdStr
                    .split(',')
                    .filter(id => id.trim() !== '')
                    .map(id => parseInt(id.trim(), 10))
                    .filter(id => !isNaN(id));

                  if (actionableIds.length === 0) {
                    return <span style={{ color: '#999' }}>暂无配置</span>;
                  }

                  const actionableNames = actionableIds
                    .map(id => {
                      const scenario = businessScenarios.find(s => s.id === id);
                      return scenario ? scenario.scenarioName : null;
                    })
                    .filter(name => name !== null);

                  return actionableNames.length > 0 ? actionableNames.join('、') : <span style={{ color: '#999' }}>暂无配置</span>;
                })()}
              </div>
            </Form.Item>
          )}

          <Form.Item
            name="attachment"
            label="附件上传"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e && e.fileList;
            }}
            extra="支持上传单个文件，格式：PDF、DOC、DOCX、TXT、JPG、PNG等，文件大小不超过10MB"
          >
            <Upload {...attachmentUploadProps}>
              <Button
                icon={<UploadOutlined />}
                loading={attachmentUploading}
                disabled={attachmentUploading}
              >
                {attachmentUploading ? '上传中...' : '选择附件'}
              </Button>
            </Upload>
          </Form.Item>

          {/* 隐藏字段用于存储attachmentId */}
          <Form.Item
            name="attachmentId"
            hidden
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default JudgeSetList;
